import { useId, useMemo, type ReactNode } from 'react';
import { CalendarCheck2 } from 'lucide-react';
import { versendetGruppen, wochenGruppen, type PlanungsMail, type WochenGruppe, type Zeitbezug } from '../../lib/planung';
import { MailKarte } from './MailKarte';
import './Planung.css';

export type ListenModus = 'anstehend' | 'versendet' | 'alle';

type Props = {
  mails: PlanungsMail[];
  heute?: Date;
  onOeffnen: (m: PlanungsMail) => void;
  aktionen?: (m: PlanungsMail) => ReactNode;
  extern?: boolean;
  /** anstehend = nach Versandwoche, versendet = neueste zuerst, alle = beides untereinander */
  modus?: ListenModus;
  /** Text, wenn nichts zu zeigen ist */
  leerText?: string;
  /** Wochen nach Versandtag (Standard) oder nach Beginn des Events; die Datumskachel folgt mit. */
  nach?: Zeitbezug;
  /** Team: versendete Mails verlinken auf die Statistik */
  team?: boolean;
};

const LEER: Record<ListenModus, string> = {
  anstehend: 'Gerade ist keine Mail geplant.',
  versendet: 'In letzter Zeit wurde keine Mail versendet.',
  alle: 'Hier ist gerade nichts.',
};

function Gruppe({ g, onOeffnen, aktionen, extern, nach, heute, team }: { g: WochenGruppe } & Pick<Props, 'onOeffnen' | 'aktionen' | 'extern' | 'nach' | 'heute' | 'team'>) {
  const id = useId();
  const ton = g.key === 'verstrichen' ? ' gm-gruppe--warn' : g.titel === 'Diese Woche' ? ' gm-gruppe--jetzt' : '';
  const n = g.mails.length;
  return (
    <section className={`gm-gruppe${ton}`} aria-labelledby={id}>
      <header className="gm-gruppe-kopf">
        <div className="gm-gruppe-text">
          <h2 id={id} className="gm-gruppe-titel">{g.titel}</h2>
          {g.unter && <span className="gm-gruppe-unter">{g.unter}</span>}
        </div>
        <span className="gm-gruppe-zahl" title={`${n} ${n === 1 ? 'Mail' : 'Mails'}`}>
          {n}<span className="sr-only"> {n === 1 ? 'Mail' : 'Mails'}</span>
        </span>
      </header>
      <ul className="gm-gruppe-liste">
        {g.mails.map((m) => (
          <li key={m.id}><MailKarte mail={m} onOeffnen={onOeffnen} aktionen={aktionen} extern={extern} datum={nach} heute={heute} team={team} /></li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Mails nach Wochen: „Termin verstrichen", diese Woche, nächste Woche …,
 * „Ohne Versandtermin" — und versendete Mails, neueste Woche zuerst.
 * Mit nach = 'event' dieselben Wochen nach Beginn des Events.
 */
export function PlanungsListe({ mails, heute, onOeffnen, aktionen, extern = false, modus = 'anstehend', leerText, nach = 'versand', team = false }: Props) {
  const tag = heute?.getTime();
  const { anstehend, versendet } = useMemo(() => {
    const h = tag !== undefined ? new Date(tag) : new Date();
    return {
      anstehend: modus === 'versendet' ? [] : wochenGruppen(mails, h, nach),
      versendet: modus === 'anstehend' ? [] : versendetGruppen(mails, nach),
    };
  }, [mails, tag, modus, nach]);

  if (anstehend.length + versendet.length === 0) {
    return (
      <div className="gm-leer">
        <CalendarCheck2 size={32} aria-hidden="true" />
        <p>{leerText ?? LEER[modus]}</p>
      </div>
    );
  }

  return (
    <div className="gm-liste">
      {anstehend.map((g) => <Gruppe key={g.key} g={g} onOeffnen={onOeffnen} aktionen={aktionen} extern={extern} nach={nach} heute={heute} team={team} />)}
      {modus === 'alle' && versendet.length > 0 && (
        <p className="gm-trenner"><span>Bereits versendet</span></p>
      )}
      {versendet.map((g) => <Gruppe key={g.key} g={g} onOeffnen={onOeffnen} aktionen={aktionen} extern={extern} nach={nach} heute={heute} team={team} />)}
    </div>
  );
}
